import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, ShieldCheck, Trash2, UserPlus, Users } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { ApiError, api } from "../api";
import { useIssuesMeta } from "../components/issues/meta";
import { Picker } from "../components/issues/picker";
import { EmptyState, PageSkeleton } from "../components/page";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { initials } from "../lib/format";
import { personLabel } from "../lib/people";
import { useIsAdmin, useIsManager } from "../lib/permissions";

export function TeamDetailPage() {
  const { key = "" } = useParams();
  const admin = useIsAdmin();
  const manager = useIsManager();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = useIssuesMeta().data;
  const query = useQuery({ queryKey: ["team", key], queryFn: () => api.team(key) });
  const team = query.data?.team;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["team", key] });
    void queryClient.invalidateQueries({ queryKey: ["teams"] });
    void queryClient.invalidateQueries({ queryKey: ["issues-meta"] });
  };
  const setMembers = useMutation({
    mutationFn: (members: Array<{ userId: string; role?: "approver" | "member" }>) =>
      api.setTeamMembers(team!.id, members),
    onSuccess: refresh,
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Update failed"),
  });
  const update = useMutation({
    mutationFn: (slackChannel: string | null) => api.updateTeam(team!.id, { slackChannel }),
    onSuccess: refresh,
    onError: (error) => toast.error(error instanceof ApiError ? error.message : "Update failed"),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTeam(team!.id),
    onSuccess: () => {
      refresh();
      navigate("/teams");
    },
  });

  if (query.isLoading) return <PageSkeleton />;
  if (!team) {
    return (
      <EmptyState icon={Users} title="Team not found">
        <Button variant="outline" asChild>
          <Link to="/teams">Back to teams</Link>
        </Button>
      </EmptyState>
    );
  }

  const memberIds = new Set(team.members.map((member) => member.id));

  return (
    <>
      <Breadcrumb className="text-xs">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/teams">Teams</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{team.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="-mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl leading-tight font-semibold tracking-[-0.022em]">
            <span className="size-3.5 rounded-sm" style={{ backgroundColor: team.color }} />
            {team.name}
            <span className="font-mono text-sm text-muted-foreground">{team.key}</span>
          </h1>
          {team.description && <p className="mt-1 text-sm text-muted-foreground">{team.description}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/issues?team=${team.key}`}>{team.issueCount} issues</Link>
          </Button>
          {admin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive">
                  <Trash2 /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {team.name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Issues keep their history; they just lose this team assignment.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()}>Delete team</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Members
              {manager && (
                <Picker
                  options={(meta?.users ?? [])
                    .filter((user) => user.kind === "human" && !memberIds.has(user.id))
                    .map((user) => ({
                      value: user.id,
                      label: personLabel(user),
                      description: user.email,
                      icon: (
                        <Avatar className="size-7">
                          <AvatarFallback className="text-[11px] font-medium">
                            {initials(personLabel(user))}
                          </AvatarFallback>
                        </Avatar>
                      ),
                    }))}
                  className="w-80"
                  align="end"
                  selected={[]}
                  onToggle={(userId) =>
                    setMembers.mutate([
                      ...team.members.map((member) => ({ userId: member.id, role: member.role })),
                      { userId },
                    ])
                  }
                  placeholder="Add a person…"
                >
                  <Button variant="outline" size="sm">
                    <UserPlus /> Add
                  </Button>
                </Picker>
              )}
            </CardTitle>
            <CardDescription>Issues reported by members are routed to this team.</CardDescription>
          </CardHeader>
          <CardContent>
            {team.members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members yet.</p>
            ) : (
              <ul className="divide-y">
                {team.members.map((member) => (
                  <li key={member.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                    <Avatar className="size-7">
                      <AvatarFallback className="text-[11px]">
                        {personLabel(member)
                          .split(/\s+/)
                          .map((part) => part[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-sm font-medium">
                        {personLabel(member)}
                        {member.role === "approver" && <ShieldCheck className="size-3.5 text-emerald-500" />}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                    </div>
                    {manager && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() =>
                            setMembers.mutate(
                              team.members.map((m) => ({
                                userId: m.id,
                                role:
                                  m.id === member.id
                                    ? m.role === "approver"
                                      ? "member"
                                      : "approver"
                                    : m.role,
                              })),
                            )
                          }
                        >
                          {member.role === "approver" ? "Remove approver" : "Make approver"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${personLabel(member)}`}
                          className="size-7"
                          onClick={() =>
                            setMembers.mutate(
                              team.members
                                .filter((m) => m.id !== member.id)
                                .map((m) => ({ userId: m.id, role: m.role })),
                            )
                          }
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="size-4" /> Slack notifications
            </CardTitle>
            <CardDescription>New issues reported by team members are posted to this channel.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {manager ? (
              <>
                <Label htmlFor="slack-channel">Channel ID or name</Label>
                <Input
                  id="slack-channel"
                  defaultValue={team.slackChannel ?? ""}
                  placeholder="#engineering or C0123456789"
                  onBlur={(event) => {
                    const channel = event.target.value.trim() || null;
                    if (channel !== team.slackChannel) update.mutate(channel);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Requires the Slack integration to be connected with permission to post in the channel.
                </p>
              </>
            ) : (
              <p className="text-sm">{team.slackChannel ? team.slackChannel : "No Slack channel"}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
